import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { getUserRole, ROLES } from '../utils/roles';
import MaintenanceGateAdmin from './MaintenanceGateAdmin';

// ══════════════════════════════════════════════════════════════════
//  PENGUMUMAN WORKSPACE  (admin sahaja)  — /admin/announce
//  - Kandungan disimpan dalam config/workspace_announce (Firestore).
//  - Modal muncul di /workspace SELEPAS gate versi selesai, had 3 kali/
//    hari/browser (logik dalam WorkspaceView).
//  - Admin boleh tukar tajuk/teks + hidup/mati TANPA deploy.
//  - "Tunjuk semula kepada semua" = bump `id` → kira semula 3-kali-hari
//    untuk setiap browser (paparan segar).
//  ⚠️ Rules WAJIB all-auth-read pada config/workspace_announce, kalau
//    tidak pelanggan bukan-admin tak boleh baca → modal mati (fail-closed).
// ══════════════════════════════════════════════════════════════════

const KOSONG = { enabled: false, title: '', body: '', id: '' };

export default function AnnounceAdminView() {
  const role = getUserRole(auth.currentUser?.email);
  const isAdmin = role === ROLES.ADMIN || role === ROLES.MODERATOR;
  const [data, setData] = useState(null);      // null = memuatkan
  const [draf, setDraf] = useState(null);       // salinan boleh-edit
  const [simpan, setSimpan] = useState('');     // '' | 'menyimpan' | 'siap' | 'ralat'
  const [asal, setAsal] = useState('');         // snapshot server terakhir (utk kesan perubahan)

  useEffect(() => {
    if (!isAdmin) return;
    const unsub = onSnapshot(
      doc(db, 'config', 'workspace_announce'),
      (snap) => {
        const d = snap.exists() ? { ...KOSONG, ...snap.data() } : { ...KOSONG };
        setData(d);
        // Isi draf HANYA kali pertama / bila belum diusik (elak timpa taipan admin).
        setDraf((prev) => (prev === null ? { enabled: !!d.enabled, title: d.title || '', body: d.body || '' } : prev));
        setAsal(JSON.stringify({ enabled: !!d.enabled, title: d.title || '', body: d.body || '' }));
      },
      () => setData({ ...KOSONG })
    );
    return () => unsub();
  }, [isAdmin]);

  if (!isAdmin) return <div style={{ padding: 40, color: '#ef4444' }}>Akses ditolak — admin sahaja.</div>;
  if (data === null || draf === null) return <div style={{ padding: 40, color: '#888' }}>Memuatkan…</div>;

  const berubah = JSON.stringify(draf) !== asal;

  const tulis = async (extra) => {
    setSimpan('menyimpan');
    try {
      await setDoc(doc(db, 'config', 'workspace_announce'), {
        enabled: !!draf.enabled,
        title: (draf.title || '').trim(),
        body: (draf.body || '').trim(),
        updatedAt: serverTimestamp(),
        ...(extra || {}),
      }, { merge: true });
      setSimpan('siap');
      setTimeout(() => setSimpan(''), 2500);
    } catch (e) {
      console.error('[Announce] simpan gagal', e);
      setSimpan('ralat');
    }
  };

  const S = {
    wrap: { maxWidth: 640, margin: '0 auto', padding: '28px 20px 60px', color: 'var(--s-ink, #111)', fontFamily: 'system-ui, sans-serif' },
    h1: { fontSize: 22, fontWeight: 800, margin: '0 0 4px' },
    sub: { fontSize: 13, color: 'var(--s-ink-2, #666)', margin: '0 0 24px', lineHeight: 1.5 },
    card: { background: 'var(--s-card, #fff)', border: '1px solid var(--s-line, rgba(0,0,0,.1))', borderRadius: 14, padding: 20, marginBottom: 18 },
    label: { display: 'block', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--s-ink-2, #666)', marginBottom: 6 },
    input: { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 9, border: '1px solid var(--s-line-strong, rgba(0,0,0,.2))', background: 'var(--s-fill, rgba(0,0,0,.02))', color: 'var(--s-ink, #111)', fontSize: 14, marginBottom: 16 },
    ta: { minHeight: 120, resize: 'vertical', lineHeight: 1.6, fontFamily: 'inherit' },
    btn: { padding: '10px 16px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
    primary: { background: 'var(--s-primary, #262626)', color: 'var(--s-primary-ink, #fafafa)' },
    ghost: { background: 'var(--s-fill, rgba(0,0,0,.05))', color: 'var(--s-ink, #111)', border: '1px solid var(--s-line, rgba(0,0,0,.12))' },
    row: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
    toggle: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 600, marginBottom: 4 },
  };

  return (
    <div style={S.wrap}>
      <h1 style={S.h1}>📣 Pengumuman Workspace</h1>
      <p style={S.sub}>
        Muncul sebagai popup bila pelanggan masuk Workspace (selepas mereka selesai kemas kini extension).
        Had <b>3 kali sehari</b> setiap browser. Pelanggan boleh tutup. Tukar di sini terus sampai semua orang — tiada perlu deploy.
      </p>

      <div style={S.card}>
        <label style={S.toggle}>
          <input type="checkbox" checked={!!draf.enabled} onChange={(e) => setDraf({ ...draf, enabled: e.target.checked })} style={{ width: 18, height: 18 }} />
          {draf.enabled ? 'Pengumuman HIDUP (pelanggan nampak)' : 'Pengumuman MATI (pelanggan tak nampak)'}
        </label>
        <p style={{ ...S.sub, margin: '2px 0 0' }}>Matikan bila pengumuman dah tak relevan.</p>
      </div>

      <div style={S.card}>
        <label style={S.label}>Tajuk</label>
        <input style={S.input} value={draf.title} maxLength={80} placeholder="cth: Kemas kini Flow (v3.9.3)"
               onChange={(e) => setDraf({ ...draf, title: e.target.value })} />
        <label style={S.label}>Isi mesej</label>
        <textarea style={{ ...S.input, ...S.ta }} value={draf.body} maxLength={1000} placeholder="Tulis mesej pengumuman di sini…"
                  onChange={(e) => setDraf({ ...draf, body: e.target.value })} />
        <div style={S.row}>
          <button style={{ ...S.btn, ...S.primary, opacity: berubah ? 1 : 0.5, cursor: berubah ? 'pointer' : 'default' }}
                  disabled={!berubah || simpan === 'menyimpan'} onClick={() => tulis()}>
            {simpan === 'menyimpan' ? 'Menyimpan…' : simpan === 'siap' ? '✓ Disimpan' : 'Simpan'}
          </button>
          {simpan === 'ralat' && <span style={{ color: '#ef4444', fontSize: 13 }}>Gagal simpan — cuba lagi.</span>}
        </div>
      </div>

      <div style={S.card}>
        <label style={S.label}>Tunjuk semula kepada semua</label>
        <p style={{ ...S.sub, margin: '0 0 12px' }}>
          Pelanggan yang dah nampak 3 kali hari ini takkan nampak lagi. Tekan ini untuk <b>set semula</b> kiraan —
          pengumuman muncul segar untuk semua orang. Guna bila anda dah kemas kini mesej dan mahu semua orang nampak versi baharu.
        </p>
        <button style={{ ...S.btn, ...S.ghost }} disabled={simpan === 'menyimpan'}
                onClick={() => tulis({ id: 'a' + Date.now() })}>
          ↻ Tunjuk semula kepada semua sekarang
        </button>
        <p style={{ ...S.sub, margin: '10px 0 0', fontSize: 12 }}>
          Kod paparan semasa: <code>{data.id || '(belum ada)'}</code>
        </p>
      </div>

      <MaintenanceGateAdmin S={S} />
    </div>
  );
}
